import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Snowflake,
  Wind,
  Wrench,
  Clock,
} from "lucide-react";

import heroImage from "../assets/hero-coldroom.jpg";
import coldroomManufactureImage from "../assets/refrigeration.jpg";
import trailerHireImage from "../assets/hvac.jpg";

// Member organisation logos (Your images)
import member1Logo from "../assets/sairac.jpeg";
import member2Logo from "../assets/saracca.jpeg";

const faqData = [
  {
    question: "What types of coldrooms do you manufacture?",
    answer:
      "We manufacture a wide range of coldrooms including walk-in freezers, cold storage for groceries, mortuary coldrooms, and customized refrigeration units.",
  },
  {
    question: "Do you provide installation and maintenance?",
    answer:
      "Yes, our certified technicians handle professional installation and ongoing maintenance to ensure optimal performance.",
  },
  {
    question: "Can I hire refrigerated trailers?",
    answer:
      "Absolutely. We offer mobile refrigerated and VIP toilet trailers for hire or purchase nationwide.",
  },
  {
    question: "Do you provide air-conditioning services?",
    answer:
      "Yes, we offer commercial and industrial air-conditioning solutions including installation, maintenance, and repairs.",
  },
];

const Home = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showLogos, setShowLogos] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowLogos(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="min-h-screen">
      {/* 🧊 HERO SECTION */}
      <section
        className="relative h-[650px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${heroImage})`,
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            South Africa’s Trusted Cooling & Climate Experts
          </h1>

          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            A proudly Level 1 BEE company with over 20 years of experience in
            coldroom manufacturing, mobile trailer hire, and air-conditioning
            services delivering reliable, energy-efficient solutions nationwide.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/services">
              <Button size="lg" className="w-full sm:w-auto">
                View Our Services <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>

            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto bg-white/10 text-white border-white hover:bg-white hover:text-foreground"
              >
                Request a Quote
              </Button>
            </Link>
          </div>

          {/* ⭐ MEMBER ORGANISATION LOGOS + TITLE */}
          <div
            className={`flex flex-col items-center justify-center gap-4 transition-all duration-700 ease-in-out
            ${showLogos ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
          >
            <p className="text-lg md:text-xl font-semibold tracking-wide text-white/90">
              Proud Members Of
            </p>

            <div className="flex items-center justify-center gap-12">
              <img
                src={member1Logo}
                alt="SAIRAC"
                className="h-20 w-auto opacity-95 drop-shadow-lg"
              />
              <img
                src={member2Logo}
                alt="SARACCA"
                className="h-20 w-auto opacity-95 drop-shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose 3A Coldrooms?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From precision manufacturing and certified installations to
              national support — we’re your full-service refrigeration and
              climate-control partner.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <Snowflake className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  Precision Manufacturing
                </h3>
                <p className="text-muted-foreground">
                  Tailor-made coldrooms, fridges, and mortuary equipment built
                  for performance and reliability.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <Wind className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  Mobile Trailers & Rentals
                </h3>
                <p className="text-muted-foreground">
                  Hire or purchase refrigerated and VIP toilet trailers,
                  manufactured to client specifications.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <Wrench className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  Certified Installation & Maintenance
                </h3>
                <p className="text-muted-foreground">
                  Professional, qualified technicians delivering top-tier
                  installation and after-sales service.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <Clock className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  Nationwide 24/7 Support
                </h3>
                <p className="text-muted-foreground">
                  Fast, reliable service across South Africa ensuring minimal
                  downtime and maximum efficiency.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Comprehensive solutions covering coldroom manufacturing, trailer
              rentals, air-conditioning, and nationwide maintenance support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="overflow-hidden">
              <img
                src={coldroomManufactureImage}
                alt="Coldroom Manufacture"
                className="w-full h-64 object-cover"
              />
              <CardContent className="pt-6">
                <h3 className="text-2xl font-semibold mb-3">
                  Coldroom Manufacture & Installation
                </h3>
                <p className="text-muted-foreground mb-4">
                  We design, manufacture, and install high-quality coldrooms,
                  freezer rooms, and mortuary equipment built for precision,
                  durability, and compliance.
                </p>
                <Link to="/services#manufacture">
                  <Button variant="outline">
                    Learn More <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <img
                src={trailerHireImage}
                alt="Trailer Hire & Air Conditioning"
                className="w-full h-64 object-cover"
              />
              <CardContent className="pt-6">
                <h3 className="text-2xl font-semibold mb-3">
                  Trailer Hire & Air-Conditioning Solutions
                </h3>
                <p className="text-muted-foreground mb-4">
                  From mobile refrigerated and VIP toilet trailers to commercial
                  and industrial air-conditioning systems — we build, install,
                  and maintain it all.
                </p>
                <Link to="/services#hire">
                  <Button variant="outline">
                    Learn More <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Have questions? We’ve got answers about our products, services,
              and capabilities.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqData.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center px-6 py-4 text-left text-lg font-semibold text-gray-800 bg-white hover:bg-gray-100"
                  >
                    {item.question}
                    <span className="ml-2">
                      {isOpen ? (
                        <svg
                          className="w-5 h-5 text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                        </svg>
                      )}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-6 py-4 text-gray-600 bg-white border-t border-gray-100">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section
        className="relative py-20 text-white bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${heroImage})`,
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            Partner with a Level 1 BEE-certified company trusted by Eskom,
            AVBOB, and major public institutions. Contact us today for a free
            consultation and quote.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              variant="outline"
              className="bg-white text-primary hover:bg-white/90"
            >
              Contact Us Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
